import { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, PlusCircle, FileText, User,
  Menu, X, Moon, Sun, Globe, LogOut, Shield, ChevronDown, ChevronRight, Home,
  Clock, Eye, Building2, ServerCog, Command, BookOpen, ShieldCheck, ShieldAlert,
  FolderArchive, Construction
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { authApi } from '@/lib/api'
import { cn, roleLabel } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import i18n from '@/lib/i18n'
import CommandPalette from '@/components/CommandPalette'
import NotificationBell from '@/components/NotificationBell'
import MaintenanceBanner from '@/components/MaintenanceBanner'
import SessionTimer from '@/components/SessionTimer'
import { useActiveDelegation } from '@/hooks/use-delegation'
import { useEntitlements } from '@/hooks/use-entitlements'

// Le etichette sono chiavi i18n, tradotte al momento del render
const navItems = [
  { path: '/', label: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { path: '/assessment/new', label: 'nav.newAssessment', icon: PlusCircle },
  { path: '/history', label: 'nav.history', icon: Clock },
  { path: '/registro', label: 'nav.registro', icon: ShieldCheck },
  { path: '/documenti', label: 'nav.documenti', icon: FolderArchive },
  { path: '/guida', label: 'nav.guida', icon: BookOpen },
]

const restrictedNavItems = [
  { path: '/governance', label: 'nav.governance', icon: LayoutDashboard, roles: ['DIRIGENTE', 'DPO'] },
  { path: '/review-queue', label: 'nav.reviewQueue', icon: Eye, roles: ['DIRIGENTE', 'DPO'] },
  { path: '/audit', label: 'nav.audit', icon: Shield, roles: ['DIRIGENTE', 'DPO'] },
  { path: '/school', label: 'nav.school', icon: Building2, roles: ['DIRIGENTE', 'DPO'] },
  { path: '/admin', label: 'nav.platform', icon: ServerCog, roles: ['PLATFORM_ADMIN'] },
  { path: '/manutenzione', label: 'nav.maintenance', icon: Construction, roles: ['PLATFORM_ADMIN'] },
]

// Chiavi i18n per il percorso di navigazione (breadcrumb) in stile Design System
const breadcrumbKeys: Record<string, string> = {
  '/assessment/new': 'nav.newAssessmentCrumb',
  '/history': 'nav.history',
  '/registro': 'nav.registro',
  '/documenti': 'nav.documenti',
  '/governance': 'nav.governance',
  '/guida': 'nav.guida',
  '/review-queue': 'nav.reviewQueueCrumb',
  '/dpo': 'nav.dpoDashboard',
  '/audit': 'nav.activityLog',
  '/school': 'nav.school',
  '/admin': 'nav.platform',
  '/manutenzione': 'nav.maintenance',
  '/profile': 'nav.profile',
}

function currentCrumbKey(pathname: string): string | null {
  if (pathname === '/') return null
  if (breadcrumbKeys[pathname]) return breadcrumbKeys[pathname]
  if (pathname.startsWith('/assessment')) return 'nav.assessmentCrumb'
  if (pathname.startsWith('/guida')) return 'nav.guida'
  return null
}

export default function Layout() {
  const { t } = useTranslation()
  const { user, logout, isDarkMode, toggleDarkMode, language, setLanguage } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN'
  const isDpo = user?.role === 'DPO'
  // Per l'admin /admin è la home (voce Dashboard), per il DPO lo è /dpo:
  // niente briciola di pane sulla propria home
  const delegation = useActiveDelegation()
  const { data: entitlements } = useEntitlements()
  // Con un piano d'istituto a pagamento la Cronologia convive con superfici
  // che mostrano l'intera scuola (Registro, Governance): la voce si
  // specifica "personale" per chiarire che resta solo la propria
  const historyLabelKey = entitlements && entitlements.plan !== 'FREE'
    ? 'nav.historyPersonal'
    : 'nav.history'
  const crumbKey =
    (isPlatformAdmin && location.pathname === '/admin') ||
    (isDpo && location.pathname === '/dpo')
      ? null
      : location.pathname === '/history'
      ? historyLabelKey
      : currentCrumbKey(location.pathname)

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch {}
    logout()
    navigate('/login')
    toast({ title: t('auth.logoutSuccess') })
  }

  const switchLanguage = () => {
    const newLang = language === 'it' ? 'en' : 'it'
    setLanguage(newLang)
    i18n.changeLanguage(newLang)
    localStorage.setItem('varia-lang', newLang)
  }

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  // Stile voce di navigazione in chiave Design System: accento laterale sull'attivo
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-r-md text-sm font-medium transition-colors border-l-[3px]',
      isActive
        ? 'bg-mim-lightblue text-mim-blue dark:bg-primary/15 dark:text-primary border-mim-blue dark:border-primary'
        : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
    )

  // L'admin di piattaforma non appartiene ad alcun tenant e accede ai
  // contenuti solo via break-glass: le voci di interazione docente↔istituto
  // spariscono e la Dashboard diventa il pannello Piattaforma.
  const visibleNav = isPlatformAdmin
    ? [
        { path: '/admin', label: 'nav.dashboard', icon: LayoutDashboard, end: true },
        { path: '/guida', label: 'nav.guida', icon: BookOpen, end: false },
      ]
    : isDpo
    ? [
        // Il DPO valida le analisi, non le richiede: niente Nuova valutazione
        // né Cronologia; il Cruscotto DPO prende il posto della Dashboard.
        { path: '/dpo', label: 'nav.dpoDashboard', icon: ShieldAlert, end: false },
        ...(user?.school_id
          ? [
              { path: '/registro', label: 'nav.registro', icon: ShieldCheck, end: false },
              { path: '/documenti', label: 'nav.documenti', icon: FolderArchive, end: false },
            ]
          : []),
        { path: '/guida', label: 'nav.guida', icon: BookOpen, end: false },
      ]
    : navItems
        .filter(({ path }) => {
          // ISPETTORE (P7) è di sola consultazione: non avrà mai valutazioni
          // proprie, quindi Cronologia resterebbe vuota.
          if (
            user?.role === 'ISPETTORE' &&
            ['/assessment/new', '/history'].includes(path)
          ) {
            return false
          }
          return true
        })
        .map((item) => (item.path === '/history' ? { ...item, label: historyLabelKey } : item))

  const visibleRestricted = restrictedNavItems.filter(({ path, roles }) => {
    if (!user || !roles.includes(user.role)) return false
    if (path === '/admin') return false // per l'admin è la voce Dashboard
    // Le superfici d'istituto richiedono l'appartenenza a una scuola: il
    // DPO cross-tenant (senza scuola) opera dal Cruscotto DPO (voce home).
    if (!user.school_id) return false
    return true
  })
  // P7: the Dirigente's active delegate reaches the governance surfaces
  if (delegation?.scope_verdetti && !visibleRestricted.some((i) => i.path === '/review-queue')) {
    visibleRestricted.unshift(
      { path: '/governance', label: 'nav.governance', icon: LayoutDashboard, roles: [] },
      { path: '/review-queue', label: 'nav.reviewQueue', icon: Eye, roles: [] },
    )
  } else if (delegation && !delegation.scope_verdetti
      && !visibleRestricted.some((i) => i.path === '/governance')) {
    visibleRestricted.unshift(
      { path: '/governance', label: 'nav.governance', icon: LayoutDashboard, roles: [] },
    )
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="ValutazIA" className="w-14 h-auto" />
          <div>
            <h1 className="font-bold text-base text-foreground leading-none">ValutazIA</h1>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">
              {t('nav.tagline')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
          {t('nav.menu')}
        </p>
        {visibleNav.map(({ path, label, icon: Icon, end }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            onClick={() => setSidebarOpen(false)}
            className={navLinkClass}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {t(label as any)}
          </NavLink>
        ))}

        {visibleRestricted.length > 0 && (
          <p className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
            {t('nav.management')}
          </p>
        )}
        {visibleRestricted.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            onClick={() => setSidebarOpen(false)}
            className={navLinkClass}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {t(label as any)}
          </NavLink>
        ))}
      </nav>

      {/* Bottom controls */}
      <div className="px-3 py-4 border-t border-border space-y-1">
        <button
          onClick={() => document.dispatchEvent(
            new KeyboardEvent('keydown', { key: 'k', ctrlKey: true })
          )}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground w-full transition-colors"
        >
          <Command className="w-4 h-4" />
          <span className="flex-1 text-left">{t('nav.searchCmd')}</span>
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-border">⌘K</kbd>
        </button>
        <button
          onClick={toggleDarkMode}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground w-full transition-colors"
        >
          {/* L'etichetta descrive l'azione (il tema verso cui si passa),
              coerente con l'icona che già cambia con il tema attivo */}
          {(isDarkMode ?? window.matchMedia('(prefers-color-scheme: dark)').matches)
            ? <><Sun className="w-4 h-4" />{t('nav.lightMode')}</>
            : <><Moon className="w-4 h-4" />{t('nav.darkMode')}</>}
        </button>
        <button
          onClick={switchLanguage}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground w-full transition-colors"
        >
          <Globe className="w-4 h-4" />
          {language === 'it' ? '🇮🇹 Italiano' : '🇬🇧 English'}
        </button>
      </div>

      {/* User section */}
      {user && (
        <div className="px-3 py-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-muted transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user.avatar_url} />
                  <AvatarFallback className="bg-mim-blue text-white text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {roleLabel(user.role)}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                {t('nav.profile')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      {/* Skip-link accessibile (WCAG / Linee Guida AgID): salta al contenuto */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:rounded-md focus:bg-mim-blue focus:text-white focus:font-semibold focus:shadow-lg"
      >
        {t('nav.skipToContent')}
      </a>
      <CommandPalette />

      {/* Barra istituzionale slim (Design system .italia) */}
      <div className="it-slim-bar flex-shrink-0">
        <div className="flex items-center justify-between px-4 lg:px-6 h-9 text-white">
          <span className="text-[11px] sm:text-xs font-semibold tracking-wide text-blue-100/90 truncate">
            AI Act · Linee Guida MIM · GDPR
          </span>
          <div className="flex items-center gap-2">
            {delegation && (
              <span
                className="hidden sm:inline-flex items-center rounded-full bg-mim-orange/90 text-white text-[10px] font-bold px-2 py-0.5"
                title={t('nav.delegationTitle', {
                  name: delegation.delegante_name ?? '',
                  date: new Date(delegation.valid_until).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB'),
                })}
              >
                {t('nav.delegationBadge', {
                  date: new Date(delegation.valid_until).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB'),
                })}
              </span>
            )}
            <NotificationBell />
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r border-border bg-card flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col lg:hidden transform transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-bold text-mim-blue">ValutazIA</span>
          <button onClick={() => setSidebarOpen(false)} aria-label={t('nav.closeMenu')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <button onClick={() => setSidebarOpen(true)} aria-label={t('nav.openMenu')}>
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold text-mim-blue">ValutazIA</span>
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className="bg-mim-blue text-white text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </header>

        <SessionTimer />
        <MaintenanceBanner />

        {/* Breadcrumb istituzionale (Design system .italia) */}
        {crumbKey && (
          <nav
            aria-label={t('nav.breadcrumb')}
            className="hidden lg:flex items-center gap-1.5 px-6 h-9 border-b border-border bg-card/60 text-xs text-muted-foreground flex-shrink-0"
          >
            <NavLink to="/" className="flex items-center gap-1 hover:text-mim-blue dark:hover:text-primary hover:underline underline-offset-2">
              <Home className="w-3.5 h-3.5" />
              {t('nav.home')}
            </NavLink>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
            <span className="text-foreground font-medium" aria-current="page">{t(crumbKey as any)}</span>
          </nav>
        )}

        {/* Page Content */}
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto outline-none">
          <div className="min-h-full flex flex-col">
            <div className="flex-1">
              <Outlet />
            </div>
            <InstitutionalFooter />
          </div>
        </main>
      </div>
      </div>
    </div>
  )
}

// Footer istituzionale (Design system .italia)
function InstitutionalFooter() {
  const { t } = useTranslation()
  return (
    <footer className="mt-auto bg-mim-darkblue text-blue-100 border-t-4 border-mim-orange">
      <div className="px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="ValutazIA" className="h-9 w-auto" />
          <div>
            <p className="font-bold text-white leading-none">ValutazIA</p>
            <p className="text-xs text-blue-200 mt-1">
              {t('nav.footerTagline')}
            </p>
          </div>
        </div>
        <div className="text-xs text-blue-200/90 space-y-1 sm:text-right">
          <p>Reg. (UE) 2024/1689 (AI Act) · DM 166/2025 (Linee Guida MIM) · Reg. (UE) 2016/679 (GDPR)</p>
          <p className="text-blue-300/70">
            {t('nav.footerDesign')}
          </p>
        </div>
      </div>
    </footer>
  )
}
